import Modal, { Prompt, ModalFooter, ModalHeader } from '@ingka/modal';
import Button from '@ingka/button';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  getSelectedEvent,
  getSelectedTimeslot,
  setError,
  setEvent,
  useAppContext,
} from '../../contextProviders/AppContext';
import { RegisterObject, TimeSlot } from '../../types/model';
import { getEvent, registerConsumer } from '../../services/storeEventApi';
import analytics from '../../analytics';

type PropsType = {
  visible: boolean;
  closeModal: any;
  registrationState: string;
};

function getRegistrationObject(timeSlot: TimeSlot): RegisterObject | null {
  return timeSlot.registration;
}

function sendCancelEvent(storeNo: string) {
  analytics.sendEvent({
    label: storeNo,
    action: 'event_signup_cancelled',
  });
}

const CancelRegistrationModal = ({
  visible,
  closeModal,
  registrationState,
}: PropsType) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { useSelector, dispatch } = useAppContext();
  const selectedTimeslot = useSelector(getSelectedTimeslot);
  const selectedEvent = useSelector(getSelectedEvent);
  const storeNo = selectedEvent?.location?.store.storeNo;

  const onCancelRegistration = async () => {
    setLoading(true);
    const registrationObject = getRegistrationObject(selectedTimeslot);

    if (!registrationObject) {
      dispatch(setError(-1));
      return;
    }

    const requestData = {
      ...registrationObject,
      state: 'CANCELLED',
    };

    try {
      await registerConsumer(
        selectedEvent.id,
        selectedTimeslot.timeslotId,
        requestData
      );
      sendCancelEvent(storeNo);
      const event = await getEvent(selectedEvent.id);
      dispatch(setEvent(event));
      setLoading(false);
      closeModal(true);
    } catch (e: any) {
      dispatch(setError(e.code));
      Sentry.captureException(e);
    }
  };

  type TextsType = {
    [key: string]: {
      title: string;
      body: string;
      button: string;
    };
  };

  const texts: TextsType = {
    REGISTERED: {
      title: 'event.cancelRegistrationModalTitle',
      body: 'event.cancelRegistrationModalBody',
      button: 'event.cancelRegistration',
    },
    CHECKIN: {
      title: 'event.cancelRegistrationModalTitle',
      body: 'event.cancelRegistrationModalBody',
      button: 'event.cancelRegistration',
    },
    SELF_CHECKIN: {
      title: 'event.cancelRegistrationModalTitle',
      body: 'event.cancelRegistrationModalBody',
      button: 'event.cancelRegistration',
    },
    WAITING_LIST: {
      title: 'event.cancelWaitingListModalTitle',
      body: 'event.cancelWaitingListModalBody',
      button: 'event.leaveWaitingList',
    },
  };

  return (
    <Modal escapable={true} visible={visible} handleCloseBtn={closeModal}>
      <Prompt
        title={t(texts[registrationState].title)}
        header={
          <ModalHeader aria-label="close prompt" ariaCloseTxt="Close prompt" />
        }
        footer={
          <ModalFooter>
            <Button
              type="primary"
              onClick={onCancelRegistration}
              text={t(texts[registrationState].button)}
              loading={loading}
            />
            <Button
              type="secondary"
              text={t('event.goBack')}
              onClick={closeModal}
              disabled={loading}
            />
          </ModalFooter>
        }
      >
        <p>{t(texts[registrationState].body)}</p>
      </Prompt>
    </Modal>
  );
};

export default CancelRegistrationModal;
